import {
  type FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';

import S from './style.scss';

const RATES = [0.9, 1, 1.1] as const;

type PropsT = {
  /** When true, always render all three rates inline (landscape). */
  expanded: boolean;

  /** Notifies the parent when the stacked popover opens/closes. */
  onOpenChange: (open: boolean) => void;

  rate: number;
  setRate: (rate: number) => void;
};

/**
 * Playback-speed control. In landscape (`expanded`) it shows ×0.9 / ×1.0 / ×1.1
 * inline. In portrait it collapses to the current rate and, on tap, the pill
 * grows upward in place (anchored at its bottom) into a vertical stack.
 */
export const SpeedPill: FunctionComponent<PropsT> = ({
  expanded,
  onOpenChange,
  rate,
  setRate,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const updateOpen = (next: boolean): void => {
    setOpen(next);
    onOpenChange(next);
  };

  // Close the stacked popover when a pointer goes down outside of it.
  useEffect(() => {
    if (!open) return undefined;
    const onDocPointerDown = (e: PointerEvent): void => {
      const el = containerRef.current;
      if (el && !el.contains(e.target as Node)) {
        setOpen(false);
        onOpenChange(false);
      }
    };
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown);
    };
  }, [open, onOpenChange]);

  const options = RATES.map((r) => (
    <button
      className={rate === r ? S.active : S.item}
      key={r}
      onClick={() => {
        setRate(r);
        updateOpen(false);
      }}
      type="button"
    >
      {`×${r.toFixed(1)}`}
    </button>
  ));

  if (expanded) {
    return <div className={S.pill}>{options}</div>;
  }

  return (
    <div className={S.container} ref={containerRef}>
      {/* Collapsed trigger; kept (hidden) while open to reserve its footprint
          so the control row doesn't reflow. */}
      <button
        className={open ? `${S.pill} ${S.hidden}` : S.pill}
        onClick={() => {
          updateOpen(true);
        }}
        type="button"
      >
        <span className={S.active}>{`×${rate.toFixed(1)}`}</span>
      </button>
      {open ? <div className={`${S.pill} ${S.stacked}`}>{options}</div> : null}
    </div>
  );
};

export default SpeedPill;
